import { InfoOutlined } from "@mui/icons-material";
import { Divider, Skeleton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { useBreakpoints } from "src/hooks";
import { CoherenceSummaryDto } from "src/http";

interface IProps {
  label?: string;
  color: string;
  teamLabel: string;
  isLoading?: boolean;
  coherenceSummary?: CoherenceSummaryDto;
}

const CoherenceScore = (props: IProps) => {
  const theme = useTheme();
  const { isMdUp } = useBreakpoints();

  return (
    <Stack spacing={{ xs: 0.5, md: 1 }}>
      <Stack>
        {props.label && (
          <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
            {props.label}
          </Typography>
        )}
        <Typography variant="h4" sx={{ color: props.color }}>
          {props.teamLabel}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={{ xs: 0.5, md: 1 }} divider={<Divider flexItem orientation="vertical" />}>
        <Stack sx={{
          flex: 1
        }}>
          <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
            Coherence
          </Typography>
          <Typography variant={isMdUp ? "h1" : "h3"} sx={{ color: props.color }}>
            {props.isLoading ? (
              <Skeleton height={"unset"} width={"100px"} />
            ) : (
              (props.coherenceSummary?.coherenceScore ?? "-")
            )}
          </Typography>
        </Stack>
        <Stack sx={{
          flex: 1
        }}>
          <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
            Responses
          </Typography>
          <Typography variant={isMdUp ? "h1" : "h3"} sx={{ color: theme.palette.text.primary }}>
            {props.isLoading ? (
              <Skeleton height={"unset"} width={"100px"} />
            ) : (
              (props.coherenceSummary?.responseCount ?? "-")
            )}
          </Typography>
        </Stack>
        <Stack sx={{
          flex: 1
        }}>
          <Stack direction="row" spacing={0.5} sx={{
            alignItems: "center"
          }}>
            <Typography variant="caption" sx={{ textTransform: "uppercase" }}>
              Response Rate
            </Typography>
            {isMdUp && (
              <Tooltip
                title={
                  <Stack>
                    <Stack direction="row" spacing={0.5} sx={{ alignItems: "baseline" }}>
                      <InfoOutlined sx={{ position: "relative", top: "2px" }} />
                      <Typography variant="body2">Response Rates</Typography>
                    </Stack>
                    <Typography variant="caption">
                      Response Rate of {props.coherenceSummary?.responseRate ?? "-"}% is based on the formula [Count of
                      Survey Responses] over [Count of Team Members]
                    </Typography>
                    <Typography variant="caption">
                      Response Rate of {props.coherenceSummary?.primaryRoleResponseRate ?? "-"}% is based on the formula
                      [Count of Survey Responses] over [Count of Primary Role Filling Team Members Only]
                    </Typography>
                  </Stack>
                }
              >
                <InfoOutlined sx={{ color: "text.secondary", alignSelf: "center" }} fontSize="small" />
              </Tooltip>
            )}
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{
            alignItems: "baseline"
          }}>
            <Typography variant={isMdUp ? "h1" : "h3"} sx={{ color: theme.palette.text.primary }}>
              {props.isLoading ? (
                <Skeleton height={"unset"} width={"100px"} />
              ) : props.coherenceSummary?.responseRate != null ? (
                `${props.coherenceSummary?.responseRate}%`
              ) : (
                "-"
              )}
            </Typography>

            {!props.isLoading && (
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: "1.125rem",
                  fontWeight: 500,
                }}
              >
                {props.coherenceSummary?.primaryRoleResponseRate != null
                  ? `${props.coherenceSummary.primaryRoleResponseRate}%`
                  : "-"}
              </Typography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export { CoherenceScore };
